/**
 * Embeddable calendar widget — no app chrome.
 *
 * Usage:
 *   <iframe src="https://kalovate.com/widget/calendar?locality=bloomington&limit=5"
 *           style="border:0;width:100%;height:500px" loading="lazy"></iframe>
 *
 * Query params:
 *   locality    — locality slug (e.g. "bloomington")
 *   category    — filter by category slug
 *   limit       — max events to show (1–50, default 10)
 */

import { useQuery } from '@tanstack/react-query';
import { CalendarDays, MapPin, ExternalLink } from 'lucide-react';

import { fetchJson } from '@/lib/queryClient';

interface WidgetEvent {
  id: string;
  title: string;
  startsAt: string;
  endsAt: string | null;
  category: string;
  attendanceMode: string;
  externalUrl: string | null;
  venueName: string | null;
}

function getSearchParams(): URLSearchParams {
  return new URLSearchParams(window.location.search);
}

export default function WidgetCalendar() {
  const params = getSearchParams();
  const locality = params.get('locality') ?? undefined;
  const category = params.get('category') ?? undefined;
  const limit = params.get('limit') ?? '10';

  const query = new URLSearchParams();
  if (locality) query.set('locality', locality);
  if (category) query.set('category', category);
  query.set('limit', limit);

  const { data, isLoading, isError } = useQuery<{
    data: WidgetEvent[];
    localityName: string | null;
  }>({
    queryKey: ['widget-calendar', locality, category, limit],
    queryFn: () =>
      fetchJson<{ data: WidgetEvent[]; localityName: string | null }>(
        `/api/widget/calendar?${query.toString()}`
      ),
    staleTime: 5 * 60 * 1000,
  });

  if (isLoading) {
    return <div className="p-4 text-sm text-gray-500 font-sans">Loading events…</div>;
  }

  if (isError || !data) {
    return <div className="p-4 text-sm text-red-600 font-sans">Failed to load events.</div>;
  }

  const events = data.data;
  const locationName = data.localityName;

  return (
    <div className="font-sans text-sm antialiased" style={{ colorScheme: 'light' }}>
      {/* Widget header */}
      <div className="flex items-center gap-2 px-4 pt-4 pb-3 border-b border-gray-200">
        <CalendarDays className="h-4 w-4 text-gray-500 shrink-0" />
        <span className="font-semibold text-gray-800">
          {locationName ? `Upcoming in ${locationName}` : 'Upcoming Events'}
          {category ? ` · ${category}` : ''}
        </span>
      </div>

      {events.length === 0 ? (
        <div className="px-4 py-6 text-center text-gray-500 text-sm">No upcoming events.</div>
      ) : (
        <ul className="divide-y divide-gray-100">
          {events.map(ev => {
            const start = new Date(ev.startsAt);
            const dateLabel = start.toLocaleDateString(undefined, {
              weekday: 'short',
              month: 'short',
              day: 'numeric',
            });
            const timeLabel = start.toLocaleTimeString(undefined, {
              hour: 'numeric',
              minute: '2-digit',
            });

            return (
              <li key={ev.id} className="flex gap-3 px-4 py-3 hover:bg-gray-50 transition-colors">
                {/* Date badge */}
                <div className="shrink-0 w-10 text-center">
                  <div className="text-xs font-semibold text-indigo-600 uppercase leading-none">
                    {start.toLocaleDateString(undefined, { month: 'short' })}
                  </div>
                  <div className="text-lg font-bold text-gray-900 leading-tight">
                    {start.getDate()}
                  </div>
                </div>

                {/* Details */}
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900 truncate leading-snug">
                    {ev.externalUrl ? (
                      <a
                        href={ev.externalUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hover:text-indigo-600"
                      >
                        {ev.title}
                      </a>
                    ) : (
                      ev.title
                    )}
                  </p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {dateLabel} · {timeLabel}
                    {ev.venueName ? (
                      <>
                        {' '}
                        <span className="inline-flex items-center gap-0.5">
                          <MapPin className="h-2.5 w-2.5" />
                          {ev.venueName}
                        </span>
                      </>
                    ) : null}
                  </p>
                </div>

                {/* External link */}
                {ev.externalUrl && (
                  <a
                    href={ev.externalUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="shrink-0 self-center text-gray-400 hover:text-indigo-600"
                    aria-label="Open event"
                  >
                    <ExternalLink className="h-3.5 w-3.5" />
                  </a>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {/* Attribution footer */}
      <div className="px-4 py-2 text-right border-t border-gray-100">
        <a
          href="https://kalovate.com"
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs text-gray-400 hover:text-gray-600"
        >
          Powered by Kalovate
        </a>
      </div>
    </div>
  );
}
